import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import type {
  User,
  LoginFormData,
  RegisterFormData,
  MFAStatus,
  TOTPSetupData,
  Session,
  ForgotPasswordFormData,
  ResetPasswordFormData,
} from '@/types';

// Transform API response (camelCase) to frontend format (snake_case)
interface ApiUser {
  id: string;
  email: string;
  emailVerified: boolean;
  name: string | null;
  picture: string | null;
  isActive: boolean;
  isSuperAdmin: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
}

function transformUser(u: ApiUser): User {
  return {
    id: u.id,
    email: u.email,
    email_verified: u.emailVerified,
    name: u.name,
    picture: u.picture,
    is_active: u.isActive ?? true,
    is_super_admin: u.isSuperAdmin ?? false,
    created_at: u.createdAt,
    updated_at: u.updatedAt,
    last_login_at: u.lastLoginAt ?? null,
  };
}

export const authApi = {
  // Get current user
  getCurrentUser: async (): Promise<User> => {
    const data = await apiGet<ApiUser>('/api/users/me');
    return transformUser(data);
  },

  // Login via JSON API. Returns { mfaRequired: true } when the account has
  // two-factor enabled — the caller must then collect a code and call verifyMfa.
  login: async (data: LoginFormData): Promise<{ user: User } | { mfaRequired: true }> => {
    const result = await apiPost<{ user?: ApiUser; mfaRequired?: boolean }>('/api/auth/login', data);
    if (result.mfaRequired) {
      return { mfaRequired: true };
    }
    return { user: transformUser(result.user!) };
  },

  // Complete the MFA challenge started by login (TOTP or backup code).
  verifyMfa: async (code: string): Promise<{ user: User }> => {
    const result = await apiPost<{ user: ApiUser }>('/api/auth/mfa', { code });
    return { user: transformUser(result.user) };
  },

  // Register via JSON API
  register: async (data: RegisterFormData): Promise<{ user: User }> => {
    const result = await apiPost<{ user: ApiUser }>('/api/auth/register', data);
    return { user: transformUser(result.user) };
  },

  // Logout via JSON API
  logout: () => apiPost<void>('/api/auth/logout', {}),

  // Get session status
  getSession: async (): Promise<{ authenticated: boolean; user: User | null }> => {
    const result = await apiGet<{ authenticated: boolean; user: ApiUser | null }>('/api/auth/session');
    return {
      authenticated: result.authenticated,
      user: result.user ? transformUser(result.user) : null,
    };
  },

  // Update profile
  updateProfile: async (data: Partial<Pick<User, 'name' | 'picture'>>): Promise<User> => {
    const result = await apiPatch<ApiUser>('/api/users/me', data);
    return transformUser(result);
  },

  // Change password
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    apiPost<void>('/api/users/me/password', data),

  // ============ MFA APIs ============

  // Get MFA status
  getMFAStatus: async (): Promise<MFAStatus> => {
    const result = await apiGet<{
      enabled: boolean;
      method: 'totp' | null;
      configuredAt: string | null;
    }>('/api/mfa/status');
    return {
      enabled: result.enabled,
      method: result.method,
      configured_at: result.configuredAt,
    };
  },

  // Setup TOTP - returns QR code and secret
  setupTOTP: async (): Promise<TOTPSetupData> => {
    const result = await apiPost<{
      secret: string;
      totpUri: string;
      qrCodeUrl?: string;
      backupCodes: string[];
    }>('/api/mfa/setup');
    return {
      secret: result.secret,
      totp_uri: result.totpUri,
      qr_code_url: result.qrCodeUrl,
      backup_codes: result.backupCodes,
    };
  },

  // Verify and enable TOTP
  enableTOTP: (data: { code: string }) =>
    apiPost<{ success: boolean }>('/api/mfa/enable', data),

  // Disable MFA
  disableMFA: (data: { code: string; password: string }) =>
    apiPost<void>('/api/mfa/disable', data),

  // Regenerate backup codes
  regenerateBackupCodes: async (data: { code: string }): Promise<string[]> => {
    const result = await apiPost<{ backupCodes: string[] }>(
      '/api/mfa/backup-codes/regenerate',
      data
    );
    return result.backupCodes;
  },

  // ============ Session APIs ============

  // Get all active sessions
  getSessions: async (): Promise<Session[]> => {
    const result = await apiGet<
      Array<{
        id: string;
        userId?: string;
        ipAddress: string | null;
        userAgent: string | null;
        deviceInfo?: {
          browser?: string;
          os?: string;
          device?: string;
        } | null;
        location?: string | null;
        createdAt: string;
        lastActivityAt?: string;
        expiresAt: string;
        isCurrent: boolean;
      }>
    >('/api/sessions');
    return result.map((s) => ({
      id: s.id,
      user_id: s.userId || '',
      ip_address: s.ipAddress,
      user_agent: s.userAgent,
      device_info: s.deviceInfo || null,
      location: s.location || null,
      created_at: s.createdAt,
      last_activity_at: s.lastActivityAt || s.createdAt,
      expires_at: s.expiresAt,
      is_current: s.isCurrent,
    }));
  },

  // Revoke a specific session
  revokeSession: (sessionId: string) =>
    apiDelete<void>(`/api/sessions/${sessionId}`),

  // Revoke all other sessions
  revokeAllOtherSessions: () =>
    apiPost<void>('/api/sessions/revoke-others'),

  // ============ Email Verification APIs ============

  // Resend verification email
  resendVerificationEmail: () =>
    apiPost<{ message: string }>('/api/auth/resend-verification'),

  // ============ Password Reset APIs ============

  // Request password reset
  forgotPassword: (data: ForgotPasswordFormData) =>
    apiPost<{ message: string }>('/api/auth/forgot-password', data),

  // Reset password with token
  resetPassword: (data: ResetPasswordFormData) =>
    apiPost<{ message: string }>('/api/auth/reset-password', data),

  // Validate reset token
  validateResetToken: (token: string) =>
    apiGet<{ valid: boolean; email?: string }>('/api/auth/reset-password/validate', {
      params: { token },
    }),
};
